import {useMutation, useQuery, useQueryClient} from "react-query";
import { getAuthToken } from "../../../../../hooks/useAuthToken";
import {useUnifyAdminStore} from "../../../AdminStore";
import * as campaignApis from './campaignApis'
import { ICampaign, ILocaleText } from "../slices/createCampaignSlice";
import { MutationStatus } from "../../../AdminStore/slices";
import { t } from 'i18next';

export function useGetAllCampaigns(archiveSwitch: boolean) {
    return useQuery(
        archiveSwitch ? 'getArchivedCampaigns' : 'getAllCampaigns',
        () => archiveSwitch ? campaignApis.fetcArchivedCampaigns(getAuthToken()) : campaignApis.fetchListOfCampaigns(getAuthToken())
    );
}

export function useCreateCampaign() {
    /**
     * Hooks
     */
    const campaignItem = useUnifyAdminStore(
        (state) => state.campaignItems
    )
    const setCreateCampaignDialogOpen = useUnifyAdminStore((state) => state.setIsCreateCampaignDialogOpen)
    const resetCampaignItem = useUnifyAdminStore((state) => state.setCampaignItem)
    const queryClient = useQueryClient();

    const setCustomToastContent = useUnifyAdminStore(state => state.setCustomToastContent)
    const setShowCustomContentToast = useUnifyAdminStore(state => state.setShowCustomContentToast)


    const applications = campaignItem.targetApplications?.length && campaignItem.targetApplications?.length > 0 ?
    campaignItem.targetApplications.map((app: any) => app.id) : null;

    const workflows =  campaignItem.targetWorkflows?.length && campaignItem.targetWorkflows?.length > 0 ?
    campaignItem.targetWorkflows.map((wflow: any) => wflow.id) : null;

    /**
     * Request
     */
    const ReqBody = {
        name: campaignItem.name,
        link: campaignItem.link,
        description: campaignItem.description,
        darkLogo: campaignItem.darkLogo,
        logo: campaignItem.logo,
        isActive: campaignItem.isActive,
        showLogo: campaignItem.showLogo,
        linkText: campaignItem.linkText,
        target: {
            workflows: workflows,
            enterpriseIds: campaignItem.targetEnterprises?.length && campaignItem.targetEnterprises?.length > 0 ? campaignItem.targetEnterprises.split(",").map(id => id.trim()) : null,
            stores: campaignItem.targetStores?.length && campaignItem.targetStores?.length > 0 ? campaignItem.targetStores.split(",").map(id => id.trim()) : null,
            applications: applications,
        }
    }

    return useMutation<Record<any, any>[],
        Error,
        void,
        unknown>(() => campaignApis.createCampaign(getAuthToken(),
        ReqBody), {
        onSuccess: () => {
            queryClient.invalidateQueries(['getAllCampaigns']);
            setCreateCampaignDialogOpen(false);
            resetCampaignItem(null);
            setCustomToastContent({type: 'Success', message: t('campaigns.toastCreateSuccess')})
            setShowCustomContentToast(true)
            
        },
        onError: (error: any) => {
            console.error('Failed to create a new Campaign:', error);
            setCustomToastContent({type: 'Error', message: t('campaigns.toastCreateError')})
            setShowCustomContentToast(true)
        },
        mutationKey: 'createNewCampaign',
    })
}

export function useArchiveCampaign() {
    const campaignItem = useUnifyAdminStore((state) => state.campaignItems);
    const ReqBody = {
        id: campaignItem.id
    }

    const setCreateCampaignDialogOpen = useUnifyAdminStore((state) => state.setIsCreateCampaignDialogOpen)
    const resetCampaignItem = useUnifyAdminStore((state) => state.setCampaignItem)
    const queryClient = useQueryClient();

    const setCustomToastContent = useUnifyAdminStore(state => state.setCustomToastContent)
    const setShowCustomContentToast = useUnifyAdminStore(state => state.setShowCustomContentToast)

    return useMutation<Record<any, any>[],
        Error,
        void,
        unknown>(() => campaignApis.archiveCampaign(getAuthToken(),
        ReqBody.id), {
        onSuccess: () => {
            queryClient.invalidateQueries(['getAllCampaigns']);
            setCreateCampaignDialogOpen(false);
            resetCampaignItem(null);
            setCustomToastContent({type: 'Success', message: t('campaigns.toastArchiveSuccess')})
            setShowCustomContentToast(true)
        },
        onError: (error: any) => {
            setCustomToastContent({type: 'Error', message: t('campaigns.toastArchiveError')})
            setShowCustomContentToast(true)
        },
        mutationKey: 'archiveCampaign',
    })
}



export function useUpdateCampaign() {
    /**
     * Hooks
     */
    const campaignItem = useUnifyAdminStore(
        (state) => state.campaignItems
    )
    const setCreateCampaignDialogOpen = useUnifyAdminStore((state) => state.setIsCreateCampaignDialogOpen)
    const resetCampaignItem = useUnifyAdminStore((state) => state.setCampaignItem)
    const queryClient = useQueryClient();

    const setCustomToastContent = useUnifyAdminStore(state => state.setCustomToastContent)
    const setShowCustomContentToast = useUnifyAdminStore(state => state.setShowCustomContentToast)

    const applications = campaignItem.targetApplications?.length && campaignItem.targetApplications?.length > 0 ?
    campaignItem.targetApplications.map((app: any) => app.id) : null;

    const workflows =  campaignItem.targetWorkflows?.length && campaignItem.targetWorkflows?.length > 0 ?
    campaignItem.targetWorkflows.map((wflow: any) => wflow.id) : null;

    /**
     * Request
     */
    const ReqBody = {
        id: campaignItem.id,
        name: campaignItem.name,
        link: campaignItem.link,
        description: campaignItem.description,
        darkLogo: campaignItem.darkLogo,
        logo: campaignItem.logo,
        isActive: campaignItem.isActive,
        showLogo: campaignItem.showLogo,
        linkText: campaignItem.linkText,
        target: {
            workflows: workflows,
            enterpriseIds: campaignItem.targetEnterprises?.length && campaignItem.targetEnterprises?.length > 0 ? campaignItem.targetEnterprises.split(",").map(id => id.trim()) : null,
            stores: campaignItem.targetStores?.length && campaignItem.targetStores?.length > 0 ? campaignItem.targetStores.split(",").map(id => id.trim()) : null,
            applications: applications,
        }
    }

    return useMutation<Record<any, any>[],
        Error,
        void,
        unknown>(() => campaignApis.updateCampaign(getAuthToken(),
        ReqBody, ReqBody.id), {
        onSuccess: () => {
            queryClient.invalidateQueries(['getAllCampaigns']);
            setCreateCampaignDialogOpen(false);
            resetCampaignItem(null);
            setCustomToastContent({type: 'Success', message: t('campaigns.toastUpdateSuccess')})
            setShowCustomContentToast(true)
        },
        onError: (error: any) => {
            console.error('Failed to update Campaign:', error);
            setCustomToastContent({type: 'Error', message: t('campaigns.toastUpdateError')})
            setShowCustomContentToast(true)
        },
        mutationKey: 'updateCampaign',
    })
}

export const useCampaignTranslator = ({ keys }: { keys: (keyof ICampaign)[] }) => {
    const mutation = useMutation<{ [key: string]: string }, unknown, { model: any; lang: string }>({
        mutationFn: async ({ model, lang }) => {
            const resp = await campaignApis.runCampaignTranslator(getAuthToken(), model);
            const data = Array.isArray(resp) ? resp : resp?.data;

            return keys.reduce((acc, key, i) => {
                const translations = data[i]?.translations;
                const found = translations?.find((t: any) => t.to === lang);
                acc[key] = found?.text ?? translations?.[0]?.text ?? "";
                return acc;
            }, {} as Record<string, string>);
        },
    });

    return { mutation };
};

export const campaignTranslationModel = (campaignItems: any, lang: string, keys: string[]) => {
    // Map app locales to Azure locales
    const azureLangMap: Record<string, string> = {
        enCA: "en",    // Canada English → neutral English
        frCA: "fr-CA", // Canada French
        enUS: "en",
    };

    const localeData = keys.map((key: string) => ({
        Text: campaignItems?.[key]?.enUS || "",
    }));

    return {
        toLocale: azureLangMap[lang] || lang,
        fromLocale: ["en"],  // assuming source is US English
        localeData,
    };
};

export const translateCampaignFields = ({
    campaignItems,
    translated,
    keys,
    lang,
    updateCreateCampaignData,
    setCustomToastContent,
    setShowCustomContentToast
}: {
    campaignItems: Partial<ICampaign>;
    translated: Record<string, string>;
    keys: (keyof ICampaign)[];
    lang: "enCA" | "frCA";
    updateCreateCampaignData: (args: { fieldName: keyof ICampaign; value: any }) => void;
    setCustomToastContent: (msg: { type: keyof typeof MutationStatus; message: string }) => void;
    setShowCustomContentToast: (v: boolean) => void;
}) => {
    keys.forEach((field) => {
        const existing = (campaignItems[field] as ILocaleText) ?? {};

        updateCreateCampaignData({
            fieldName: field,
            value: {
                enUS: existing.enUS ?? "",
                enCA: existing.enCA ?? "",
                frCA: existing.frCA ?? "",
                [lang]: translated[field] ?? "",
            },
        });
    });

    setCustomToastContent({
        type: MutationStatus.Success,
        message: "Translation completed.",
    });
    setShowCustomContentToast(true);
};
